import React from "react";
import { Box, Typography, Chip, Paper, Divider } from "@mui/material";
import styled from "styled-components";
import colors from "../../theme/colors";
import {
  ArrowUpIcon,
  ArrowDownIcon,
  BatteryChargingIcon,
} from "../../theme/icons";

const WARNING_COLOR = "#f5a623";
const INACTIVE_COLOR_OPACITY = 0.6;

const MetricBoxesContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 15px 20px;

  @media (max-width: 638px) {
    padding: 0 0 20px 0;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  @media print {
    display: none !important;
  }
`;

const MetricBox = styled(Paper)`
  padding: 20px 15px;
  display: flex;
  flex-direction: column;
  justify-content: left;
  flex: 1;
  min-width: 270px;
  @media (max-width: 638px) {
    width: 100%;
  }
`;

const HeaderSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled(Typography)`
  && {
    font-weight: bold;
    color: ${colors.grayDarkText};
    letter-spacing: 1px;
    font-size: 1rem;
  }
`;

const StatusChip = styled(Chip)`
  && {
    font-weight: bold;
    border-radius: 12px;
    border-color: ${colors.grayLight};
    color: ${colors.grayDarkText};
    background-color: ${colors.grayLightest};
    cursor: default;

    &.green {
      border-color: ${colors.green};
      color: ${colors.green};
      background-color: ${colors.greenLightestSuccess};
    }

    &.red {
      border-color: ${colors.redError};
      color: ${colors.redError};
      background-color: ${colors.redLightError};
    }

    &.monitor {
      border-color: ${WARNING_COLOR};
      color: ${WARNING_COLOR};
      background-color: rgba(245, 166, 35, 0.12);
    }

    &.gray {
      border-color: ${colors.gray};
      color: ${colors.gray};
      background-color: ${colors.grayLightest};
    }
  }
`;

const GaugeWrapper = styled(Box)`
  position: relative;
  width: 250px;
  height: 140px;
  flex-shrink: 0;
`;

const GaugeArcOverlay = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 250px;
  height: 140px;
  overflow: visible;
  pointer-events: none;
  z-index: 10;
`;

const ValueTickOverlay = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 250px;
  height: 140px;
  overflow: visible;
  pointer-events: none;
  z-index: 20;
`;

const CenterDotOverlay = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 250px;
  height: 140px;
  overflow: visible;
  pointer-events: none;
  z-index: 20;
`;

const ChartAreaWrapper = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px auto;
  width: 100%;
  height: 150px;
`;

const CenterTextWrapper = styled(Box)`
  position: absolute;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: nowrap;
  z-index: 30;
`;

const CenterValue = styled(Typography)`
  && {
    font-weight: bold;
    font-size: 1.9rem;
    line-height: 1;
    color: ${colors.grayDarkText};
  }
`;

const CenterUnit = styled(Typography)`
  && {
    font-weight: bold;
    font-size: 0.85rem;
    line-height: 1;
    color: ${colors.grayDarkText};
    margin-top: 6px;
  }
`;

const LimitLabelRow = styled(Box)`
  position: relative;
  width: 250px;
  height: 22px;
  margin-top: -8px;
`;

const LimitLabel = styled(Typography)`
  && {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    color: ${colors.grayDarkText};
    font-size: 11px;
    text-align: center;
    white-space: nowrap;
  }
`;

const PercentFullLabel = styled(Typography)`
  && {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    color: ${colors.grayDarkText};
    font-size: 11px;
    font-weight: bold;
    text-align: center;
    white-space: nowrap;
  }
`;

const FooterSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 11px;
  position: relative;
`;

const TrendRow = styled(Box)`
  display: flex;
  align-items: center;

  color: ${(props) => {
    switch (props.$status) {
      case "green":
        return colors.green;
      case "orange":
        return WARNING_COLOR;
      case "red":
        return colors.redError;
      default:
        return colors.gray;
    }
  }};
`;

const TrendText = styled.div`
  font-weight: bold;
`;

const AverageText = styled.div`
  color: ${colors.grayDarkText};
  display: block;
  margin-top: 4px;
`;

const StyledDivider = styled(Divider)`
  && {
    border-color: ${colors.grayLight};
    margin-left: 8px;
    margin-right: 8px;
  }
`;

const RightFooterColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  min-height: 42px;
`;

const ThresholdLabel = styled.div`
  color: ${colors.grayDarkText};
  display: block;
  line-height: 1.2;
`;

const ThresholdValue = styled.div`
  color: ${colors.grayDarkText};
  font-weight: bold;
`;

const ChargingIndicator = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.green};
  font-weight: bold;
  font-size: 11px;
  margin-top: 4px;
  align-self: flex-end;
  height: 16px;
`;

export default React.memo(MetricGaugeCards);

function MetricGaugeCards({ metrics = [] }) {
  return (
    <MetricBoxesContainer>
      {metrics.map((metric, index) =>
        metric.render ? (
          metric.render()
        ) : (
          <MetricGaugeCard key={index} metricIndex={index} {...metric} />
        ),
      )}
    </MetricBoxesContainer>
  );
}

function MetricGaugeCard({
  metricTitle = "Weight",
  hasAlarm = false,
  metricValue = 0,
  metricMaxValue = 1,
  metricMinValue = 0,
  metricUnit = "kg",
  metricTrend = 0,
  metricTrendDirection = "up",
  metricTrendPeriod = "last 24h",
  metricAverage = 0,
  metricAveragePeriod = "30d",
  metricAverageUnitDisplay = true,
  metricAlarmThreshold = 0,
  metricAlarmThresholdOperator = "<",
  metricAlarmType = "Low Weight Alarm",
  isCharging = false,
  isLoading = true,
  metricIndex = 0,
  displayPercentFull = false,
  displayMetricAverage = true,
}) {
  const clampPercent = (value) => Math.max(0, Math.min(1, value));

  const metricRange = metricMaxValue - metricMinValue;

  const thresholdPercent =
    metricRange > 0
      ? clampPercent((metricAlarmThreshold - metricMinValue) / metricRange)
      : 0;

  const orangeRangePercent = 0.1;

  const isLowAlarm = metricAlarmThresholdOperator.startsWith("<");

  const orangeStartPercent = isLowAlarm
    ? thresholdPercent
    : clampPercent(thresholdPercent - orangeRangePercent);

  const orangeEndPercent = isLowAlarm
    ? clampPercent(thresholdPercent + orangeRangePercent)
    : thresholdPercent;

  const spectrumSegments = isLowAlarm
    ? [
        {
          key: "red",
          start: 0,
          end: thresholdPercent,
          color: colors.guageRedStart,
        },
        {
          key: "orange",
          start: thresholdPercent,
          end: orangeEndPercent,
          color: WARNING_COLOR,
        },
        {
          key: "green",
          start: orangeEndPercent,
          end: 1,
          color: colors.guageGreenStart,
        },
      ]
    : [
        {
          key: "green",
          start: 0,
          end: orangeStartPercent,
          color: colors.guageGreenStart,
        },
        {
          key: "orange",
          start: orangeStartPercent,
          end: thresholdPercent,
          color: WARNING_COLOR,
        },
        {
          key: "red",
          start: thresholdPercent,
          end: 1,
          color: colors.guageRedStart,
        },
      ];

  /*
   * Query-string testing:
   *
   * ?color=green
   * ?color=orange
   * ?color=red
   * ?color=mixed
   */
  const queryColor =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("color")?.toLowerCase()
      : null;

  const validTestColors = ["green", "orange", "red"];
  const mixedTestColors = ["green", "orange", "red"];

  const testColor =
    queryColor === "mixed"
      ? mixedTestColors[metricIndex % mixedTestColors.length]
      : queryColor;

  const isTestMode = testColor != null && validTestColors.includes(testColor);

  const testSegment = isTestMode
    ? spectrumSegments.find((segment) => segment.key === testColor)
    : null;

  const clampedMetricValue = Math.max(
    metricMinValue,
    Math.min(metricMaxValue, metricValue),
  );

  const realValuePercent =
    metricRange > 0
      ? clampPercent((clampedMetricValue - metricMinValue) / metricRange)
      : 0;

  const testValuePercent =
    testSegment && testSegment.end > testSegment.start
      ? (testSegment.start + testSegment.end) / 2
      : realValuePercent;

  const valuePercent = isTestMode ? testValuePercent : realValuePercent;

  const percentFull = Math.round(valuePercent * 100);

  const effectiveMetricValue = isTestMode
    ? metricMinValue + valuePercent * metricRange
    : clampedMetricValue;

  const effectiveIsLoading = isLoading && !isTestMode;

  const displayValue = effectiveIsLoading
    ? "--"
    : isTestMode
      ? Number(effectiveMetricValue.toFixed(2))
      : metricValue;

  /*
   * Current live metric state.
   *
   * This controls:
   * - active gauge color
   * - trend color
   *
   * It does NOT depend on hasAlarm.
   */
  let activeColorScenario;

  if (isLowAlarm) {
    if (valuePercent < thresholdPercent) {
      activeColorScenario = "red";
    } else if (valuePercent < orangeEndPercent) {
      activeColorScenario = "orange";
    } else {
      activeColorScenario = "green";
    }
  } else {
    if (valuePercent > thresholdPercent) {
      activeColorScenario = "red";
    } else if (valuePercent >= orangeStartPercent) {
      activeColorScenario = "orange";
    } else {
      activeColorScenario = "green";
    }
  }

  /*
   * Pill state.
   *
   * hasAlarm represents the persisted alarm state and
   * overrides all other pill states.
   *
   * If there is no active alarm:
   * green  -> healthy
   * orange -> monitor
   * red    -> monitor
   */
  let statusChipColorClass;
  let statusChipLabel;

  if (effectiveIsLoading) {
    statusChipColorClass = "gray";
    statusChipLabel = "loading";
  } else if (hasAlarm) {
    statusChipColorClass = "red";
    statusChipLabel = "alarm";
  } else if (
    activeColorScenario === "orange" ||
    activeColorScenario === "red"
  ) {
    statusChipColorClass = "monitor";
    statusChipLabel = "monitor";
  } else {
    statusChipColorClass = "green";
    statusChipLabel = "healthy";
  }

  /*
   * Gauge geometry
   */
  const centerX = 125;
  const centerY = 125;

  const outerRadius = 105;
  const innerRadius = outerRadius * 0.85;
  const arcRadius = innerRadius + (outerRadius - innerRadius) / 2;
  const arcWidth = outerRadius - innerRadius;

  const percentToAngle = (percent) => Math.PI - clampPercent(percent) * Math.PI;

  const getArcPoint = (percent, radius = arcRadius) => {
    const angle = percentToAngle(percent);

    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY - radius * Math.sin(angle),
    };
  };

  const createArcPath = (startPercent, endPercent) => {
    if (endPercent <= startPercent) {
      return null;
    }

    const start = getArcPoint(startPercent);
    const end = getArcPoint(endPercent);

    return `
      M ${start.x} ${start.y}
      A ${arcRadius} ${arcRadius}
      0 0 1
      ${end.x} ${end.y}
    `;
  };

  const backgroundArcPath = createArcPath(0, 1);

  const displaySegments = spectrumSegments
    .map((segment) => ({
      ...segment,
      active: segment.key === activeColorScenario,
    }))
    .filter((segment) => segment.end > segment.start);

  /*
   * Current-value tick
   */
  const valueAngle = percentToAngle(valuePercent);

  const tickStartRadius = innerRadius + 7;
  const tickEndRadius = innerRadius - 16;

  const tickOuterX = centerX + tickStartRadius * Math.cos(valueAngle);

  const tickOuterY = centerY - tickStartRadius * Math.sin(valueAngle);

  const tickInnerX = centerX + tickEndRadius * Math.cos(valueAngle);

  const tickInnerY = centerY - tickEndRadius * Math.sin(valueAngle);

  const tickVisibleWidth = 4;
  const tickOutlineWidth = tickVisibleWidth + 3;

  const centerDotDistance = 0;

  const centerDotX = centerX + centerDotDistance * Math.cos(valueAngle);

  const centerDotY = centerY - centerDotDistance * Math.sin(valueAngle);

  const centerDotRadius = tickVisibleWidth / 2 + 1;

  const minLabelPoint = getArcPoint(0);
  const maxLabelPoint = getArcPoint(1);

  return (
    <MetricBox>
      <HeaderSection>
        <Title variant="h6">{metricTitle.toUpperCase()}</Title>

        <StatusChip
          label={statusChipLabel}
          variant="outlined"
          size="small"
          className={statusChipColorClass}
          onClick={() => {}}
        />
      </HeaderSection>

      <ChartAreaWrapper>
        <GaugeWrapper>
          <GaugeArcOverlay
            viewBox="0 0 250 140"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d={backgroundArcPath}
              fill="none"
              stroke={colors.guageBackground}
              strokeWidth={arcWidth}
              strokeLinecap="round"
            />

            {!effectiveIsLoading &&
              displaySegments.map((segment, index) => {
                const path = createArcPath(segment.start, segment.end);

                if (!path) {
                  return null;
                }

                const isFirstSegment = index === 0;
                const isLastSegment = index === displaySegments.length - 1;

                return (
                  <g
                    key={`${segment.key}-${segment.start}-${segment.end}`}
                    opacity={segment.active ? 1 : INACTIVE_COLOR_OPACITY}
                  >
                    <path
                      d={path}
                      fill="none"
                      stroke={segment.color}
                      strokeWidth={arcWidth}
                      strokeLinecap="butt"
                    />

                    {isFirstSegment && segment.start === 0 && (
                      <circle
                        cx={getArcPoint(0).x}
                        cy={getArcPoint(0).y}
                        r={arcWidth / 2}
                        fill={segment.color}
                      />
                    )}

                    {isLastSegment && segment.end === 1 && (
                      <circle
                        cx={getArcPoint(1).x}
                        cy={getArcPoint(1).y}
                        r={arcWidth / 2}
                        fill={segment.color}
                      />
                    )}
                  </g>
                );
              })}
          </GaugeArcOverlay>

          {!effectiveIsLoading && (
            <ValueTickOverlay
              viewBox="0 0 250 140"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1={tickOuterX}
                y1={tickOuterY}
                x2={tickInnerX}
                y2={tickInnerY}
                stroke="#ffffff"
                strokeWidth={tickOutlineWidth}
                strokeLinecap="round"
                fill="none"
              />

              <line
                x1={tickOuterX}
                y1={tickOuterY}
                x2={tickInnerX}
                y2={tickInnerY}
                stroke={colors.grayDarkText}
                strokeWidth={tickVisibleWidth}
                strokeLinecap="round"
                fill="none"
              />
            </ValueTickOverlay>
          )}

          {!effectiveIsLoading && (
            <CenterDotOverlay
              viewBox="0 0 250 140"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx={centerDotX}
                cy={centerDotY}
                r={centerDotRadius}
                fill={colors.grayDarkText}
              />
            </CenterDotOverlay>
          )}

          <CenterTextWrapper>
            <CenterValue>{displayValue}</CenterValue>

            <CenterUnit>{metricUnit}</CenterUnit>
          </CenterTextWrapper>
        </GaugeWrapper>

        <LimitLabelRow>
          <LimitLabel
            variant="caption"
            style={{
              left: `${minLabelPoint.x}px`,
            }}
          >
            {metricMinValue}
          </LimitLabel>

          {displayPercentFull && !effectiveIsLoading && (
            <PercentFullLabel variant="caption">
              {percentFull}% full
            </PercentFullLabel>
          )}

          <LimitLabel
            variant="caption"
            style={{
              left: `${maxLabelPoint.x}px`,
            }}
          >
            {metricMaxValue}
          </LimitLabel>
        </LimitLabelRow>
      </ChartAreaWrapper>

      <FooterSection>
        <Box>
          <TrendRow $status={activeColorScenario}>
            {metricTrendDirection === "up" ? (
              <ArrowUpIcon
                sx={{
                  fontSize: "inherit",
                  mr: 0.5,
                }}
              />
            ) : (
              <ArrowDownIcon
                sx={{
                  fontSize: "inherit",
                  mr: 0.5,
                }}
              />
            )}

            <TrendText>
              {metricTrend}
              {metricUnit} {metricTrendPeriod}
            </TrendText>
          </TrendRow>

          {displayMetricAverage && (
            <AverageText>
              {metricAveragePeriod} {metricAverageUnitDisplay ? "Avg" : ""}:{" "}
              {metricAverage.toFixed(1)}{" "}
              {metricAverageUnitDisplay ? metricUnit : ""}
            </AverageText>
          )}
        </Box>

        <StyledDivider orientation="vertical" flexItem />

        <RightFooterColumn>
          <Box>
            <ThresholdLabel>{metricAlarmType}:</ThresholdLabel>

            <ThresholdValue>
              {metricAlarmThresholdOperator} {metricAlarmThreshold.toFixed(2)}{" "}
              {metricUnit}
            </ThresholdValue>
          </Box>

          <ChargingIndicator>
            {isCharging && (
              <>
                <BatteryChargingIcon
                  sx={{
                    fontSize: 14,
                  }}
                />

                <span>charging</span>
              </>
            )}
          </ChargingIndicator>
        </RightFooterColumn>
      </FooterSection>
    </MetricBox>
  );
}
